import React, {useEffect, useMemo, useState} from 'react';
import {CircularProgress} from '@mui/material';
import {Activity, Calendar, TrendingUp} from 'lucide-react';
import api from '../../../../api/axios';
import SystemHistoryChart from './SystemHistoryChart';
import {useCurrency} from '../../../../hooks/useCurrency';

export type MetricType = 'TRADES' | 'WITHDRAWS' | 'DEPOSITS' | 'USERS' | 'NEWSLETTER';
export type Timeframe = '24h' | '7d' | '30d' | '3M' | '6M' | '1Y' | 'ALL';
export type ViewMode = 'ABSOLUTE' | 'MONEY' | 'NEW_USERS' | 'CONCURRENT';

export interface SystemStat {
  day: string;
  count: number;
  amount: number;
  concurrent?: number;
}

type AllStatsData = Record<MetricType, SystemStat[]>;

const SystemHistory: React.FC = () => {
  const {currencySymbol, formatAmountWithoutCurrency, convertFromUSD} = useCurrency();
  const [loading, setLoading] = useState(true);
  const [activeMetric, setActiveMetric] = useState<MetricType>('TRADES');
  const [viewMode, setViewMode] = useState<ViewMode>('ABSOLUTE');
  const [range, setRange] = useState<Timeframe>('7d');
  const [store, setStore] = useState<AllStatsData>({
    TRADES: [], WITHDRAWS: [], DEPOSITS: [], USERS: [], NEWSLETTER: []
  });

  const timeframes: Timeframe[] = ['24h', '7d', '30d', '3M', '6M', '1Y', 'ALL'];

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        // Use allSettled to prevent single 500 errors from crashing the whole dashboard
        const results = await Promise.allSettled([
          api.get('/trade-stats'),
          api.get('/deposit-stats'),
          api.get('/withdrawl-stats'),
          api.get('/user-stats'),
          api.get('/newsletter-stats')
        ]);

        const [tradesRes, depositsRes, withdrawsRes, usersRes, newsletterRes] = results;

        const processData = (raw: any, metric: MetricType): SystemStat[] => {
          // Guard clause if endpoint failed and returned no data
          if (!raw) return [];

          const mergedMap = new Map<string, SystemStat>();
          const helper = (list: any[], key: keyof SystemStat, field: string) => {
            if (!list || !Array.isArray(list)) return;
            list.forEach(item => {
              const d = item.day;
              if (!d) return; // Guard clause against missing day field
              const ex = mergedMap.get(d) || {day: d, count: 0, amount: 0, concurrent: 0};
              (ex as any)[key] = item[field] ?? 0;
              mergedMap.set(d, ex);
            });
          };

          if (metric === 'TRADES') {
            helper(raw.countDTO, 'count', 'count');
            helper(raw.valueDTO, 'amount', 'totalValue');
          } else if (metric === 'WITHDRAWS' || metric === 'DEPOSITS') {
            helper(raw.count || raw.countDTO, 'count', 'count');
            helper(raw.valueDTO, 'amount', 'totalValue');
          } else if (metric === 'USERS') {
            helper(raw.totalUser, 'count', 'count');
            helper(raw.newUser, 'amount', 'count');
            helper(raw.maxConcurrentUser, 'concurrent', 'count');
          } else if (metric === 'NEWSLETTER') {
            helper(raw.subscriber, 'count', 'count');
          }
          return Array.from(mergedMap.values());
        };

        // Added optional chaining (?.) to prevent reading .data from undefined value on rejected promises
        setStore({
          TRADES: processData(tradesRes && tradesRes.status === 'fulfilled' ? tradesRes.value?.data : null, 'TRADES'),
          DEPOSITS: processData(depositsRes && depositsRes.status === 'fulfilled' ? depositsRes.value?.data : null, 'DEPOSITS'),
          WITHDRAWS: processData(withdrawsRes && withdrawsRes.status === 'fulfilled' ? withdrawsRes.value?.data : null, 'WITHDRAWS'),
          USERS: processData(usersRes && usersRes.status === 'fulfilled' ? usersRes.value?.data : null, 'USERS'),
          NEWSLETTER: processData(newsletterRes && newsletterRes.status === 'fulfilled' ? newsletterRes.value?.data : null, 'NEWSLETTER'),
        });
      } catch (err) {
        console.error('Initial fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  const filteredStats = useMemo(() => {
    const currentData = store[activeMetric] || []; // Guard fallback to empty array
    if (range === 'ALL') return currentData;
    const cutoff = new Date();
    switch (range) {
      case '24h':
        cutoff.setHours(cutoff.getHours() - 24);
        break;
      case '7d':
        cutoff.setDate(cutoff.getDate() - 7);
        break;
      case '30d':
        cutoff.setDate(cutoff.getDate() - 30);
        break;
      case '3M':
        cutoff.setMonth(cutoff.getMonth() - 3);
        break;
      case '6M':
        cutoff.setMonth(cutoff.getMonth() - 6);
        break;
      case '1Y':
        cutoff.setFullYear(cutoff.getFullYear() - 1);
        break;
    }
    return currentData.filter(s => s && s.day && new Date(s.day).getTime() >= cutoff.getTime());
  }, [store, activeMetric, range]);

  // Calculations for the footer
  const statsSummary = useMemo(() => {
    let total = 0;
    let peak = 0;

    if (!filteredStats || filteredStats.length === 0) {
      return {total, peak};
    }

    filteredStats.forEach(s => {
      if (!s) return;
      let val = 0;
      if (viewMode === 'MONEY') val = s.amount;
      else if (viewMode === 'NEW_USERS') val = s.amount;
      else if (viewMode === 'CONCURRENT') val = s.concurrent || 0;
      else val = s.count;

      total += val;
      if (val > peak) peak = val;
    });

    // Special case: USERS Total is usually the last known value, not a sum
    if (activeMetric === 'USERS' && viewMode === 'ABSOLUTE' && filteredStats.length > 0) {
      const sorted = [...filteredStats]
      .filter(s => s && s.day)
      .sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
      if (sorted.length > 0) {
        total = sorted[sorted.length - 1].count;
      }
    }

    return {total, peak};
  }, [filteredStats, viewMode, activeMetric]);

  return (
      <div className="mt-6 bg-neutral-900/40 backdrop-blur-md border border-neutral-800 rounded-lg p-4 font-mono text-white">
        {/* Header & Controls */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-4">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <Calendar className="text-blue-500 w-5 h-5"/>
            </div>
            <div>
              <h3 className="text-sm font-black uppercase tracking-widest">System Analytics</h3>
              <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-tighter">
                {activeMetric} / {range}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {activeMetric === 'USERS' && (
                <div className="flex bg-neutral-800/50 p-1 rounded-lg border border-white/5">
                  <button onClick={() => setViewMode('ABSOLUTE')}
                          className={`px-3 py-1 text-[9px] font-black uppercase transition-all rounded ${viewMode === 'ABSOLUTE' ? 'bg-neutral-700 text-white' : 'text-neutral-500 hover:text-neutral-300'}`}>Total
                  </button>
                  <button onClick={() => setViewMode('NEW_USERS')}
                          className={`px-3 py-1 text-[9px] font-black uppercase transition-all rounded ${viewMode === 'NEW_USERS' ? 'bg-blue-600 text-white' : 'text-neutral-500 hover:text-neutral-300'}`}>New
                  </button>
                  <button onClick={() => setViewMode('CONCURRENT')}
                          className={`px-3 py-1 text-[9px] font-black uppercase transition-all rounded ${viewMode === 'CONCURRENT' ? 'bg-orange-600 text-white' : 'text-neutral-500 hover:text-neutral-300'}`}>Concurrent
                  </button>
                </div>
            )}

            {['TRADES', 'WITHDRAWS', 'DEPOSITS'].includes(activeMetric) && (
                <div className="flex bg-neutral-800/50 p-1 rounded-lg border border-white/5">
                  <button onClick={() => setViewMode('ABSOLUTE')}
                          className={`px-3 py-1 text-[9px] font-black uppercase transition-all rounded ${viewMode === 'ABSOLUTE' ? 'bg-neutral-700 text-white' : 'text-neutral-500 hover:text-neutral-300'}`}>Count
                  </button>
                  <button onClick={() => setViewMode('MONEY')}
                          className={`px-3 py-1 text-[9px] font-black uppercase transition-all rounded ${viewMode === 'MONEY' ? 'bg-emerald-600 text-white' : 'text-neutral-500 hover:text-neutral-300'}`}>Value
                  </button>
                </div>
            )}

            <div className="flex bg-neutral-800/50 p-1 rounded-lg border border-white/5 ml-2">
              {timeframes.map((tf) => (
                  <button key={tf} onClick={() => setRange(tf)}
                          className={`px-3 py-1 text-[10px] font-black uppercase transition-all rounded ${range === tf ? 'bg-blue-600 text-white' : 'text-neutral-500 hover:text-neutral-300'}`}>
                    {tf}
                  </button>
              ))}
            </div>
          </div>
        </div>

        {/* Metric Selector */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-neutral-800 pb-4">
          {(['TRADES', 'DEPOSITS', 'WITHDRAWS', 'USERS', 'NEWSLETTER'] as MetricType[]).map((m) => (
              <button key={m} onClick={() => {
                setActiveMetric(m);
                setViewMode('ABSOLUTE');
              }}
                      className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest transition-all border ${activeMetric === m ? 'bg-white/5 border-blue-500 text-blue-400' : 'border-transparent text-neutral-500 hover:text-neutral-300'}`}>
                {m}
              </button>
          ))}
        </div>

        {/* Chart */}
        <div className="h-[340px] w-full">
          {loading ? (
              <div className="w-full h-full flex items-center justify-center"><CircularProgress size={30} sx={{color: '#3b82f6'}}/></div>
          ) : (!filteredStats || filteredStats.length === 0) ? (
              <div className="w-full h-full flex items-center justify-center text-neutral-500 text-xs uppercase tracking-wider border border-dashed border-neutral-800 rounded">
                No data available for this metric
              </div>
          ) : (
              <SystemHistoryChart data={filteredStats} range={range} metric={activeMetric} viewMode={viewMode}/>
          )}
        </div>

        {/* Footer Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-6 border-t border-neutral-800">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-3 h-3 text-neutral-500"/>
              <p className="text-[9px] font-black text-neutral-500 uppercase tracking-widest">
                {activeMetric === 'USERS' && viewMode === 'ABSOLUTE' ? 'Total Users' : 'Range Total'}
              </p>
            </div>
            <p className="text-lg font-black text-white">
              {viewMode === 'MONEY'
                  ? `${currencySymbol}${formatAmountWithoutCurrency(convertFromUSD(statsSummary.total))}`
                  : statsSummary.total.toLocaleString()
              }
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-3 h-3 text-neutral-500"/>
              <p className="text-[9px] font-black text-neutral-500 uppercase tracking-widest">Peak Activity</p>
            </div>
            <p className="text-lg font-black text-white">
              {viewMode === 'MONEY'
                  ? `${currencySymbol}${formatAmountWithoutCurrency(convertFromUSD(statsSummary.peak))}`
                  : statsSummary.peak.toLocaleString()
              }
            </p>
          </div>
          {activeMetric === 'USERS' && (
              <div className="col-span-2 text-right">
                <p className="text-[9px] font-black text-neutral-500 uppercase tracking-widest mb-1">User Status</p>
                <p className="text-lg font-black text-emerald-500 uppercase italic">System Healthy</p>
              </div>
          )}
        </div>
      </div>
  );
};

export default SystemHistory;